import { readFile, writeFile } from 'node:fs/promises';
import { join } from 'node:path';
import chalk from 'chalk';
import ora from 'ora';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';
import { loadBaseline, getBaselineStats } from '../../engine/baseline.js';
import { gitleaksRunner, semgrepRunner, osvScannerRunner, runBuiltinChecks } from '../../tools/index.js';
import type { CheckResult } from '../../types/index.js';

interface ReportOptions {
  format?: 'console' | 'json' | 'markdown';
  output?: string;
}

interface PostureScore {
  overall: number;
  categories: {
    secrets: { score: number; issues: number };
    deps: { score: number; issues: number };
    security: { score: number; issues: number };
    tests: { score: number; issues: number };
    config: { score: number; issues: number };
  };
  trend?: 'improving' | 'stable' | 'declining';
  previousScore?: number;
}

export async function reportCommand(options: ReportOptions): Promise<void> {
  const cwd = process.cwd();
  const format = options.format ?? 'console';
  const spinner = ora('Generating security posture report...').start();

  try {
    const loaded = await loadConfig(cwd);
    const config = loaded?.config ?? getDefaultConfig();

    // Run all checks
    const allResults: CheckResult[] = [];

    spinner.text = 'Running builtin checks...';
    const builtinResults = await runBuiltinChecks(cwd, [
      'secrets.gitignore-required',
      'secrets.no-env-commit',
      'deps.lockfile.required',
      'deps.no-unpinned',
      'deps.no-git-deps',
      'tests.unit.required',
      'code.strict-mode',
      'config.node-version',
      'config.editor-config',
    ]);
    allResults.push(...builtinResults);

    if (await gitleaksRunner.isInstalled()) {
      spinner.text = 'Running secret scan...';
      const results = await gitleaksRunner.run(cwd);
      allResults.push(...results);
    }

    if (await osvScannerRunner.isInstalled()) {
      spinner.text = 'Running dependency scan...';
      const results = await osvScannerRunner.run(cwd);
      allResults.push(...results);
    }

    // Load baseline for trend
    const baseline = await loadBaseline(cwd);
    const baselineStats = getBaselineStats(allResults, baseline);

    // Calculate posture score
    const posture = calculatePosture(allResults, baseline);

    spinner.stop();

    if (format === 'json') {
      const report = {
        timestamp: new Date().toISOString(),
        posture,
        issues: allResults,
        baseline: baseline ? baselineStats : undefined,
      };
      
      if (options.output) {
        await writeFile(options.output, JSON.stringify(report, null, 2));
        console.log(chalk.green(`Report saved to ${options.output}`));
      } else {
        console.log(JSON.stringify(report, null, 2));
      }
    } else if (format === 'markdown') {
      const md = generateMarkdownReport(posture, allResults, baselineStats);
      
      if (options.output) {
        await writeFile(options.output, md);
        console.log(chalk.green(`Report saved to ${options.output}`));
      } else {
        console.log(md);
      }
    } else {
      printConsoleReport(posture, allResults, baselineStats);
    }
  } catch (error) {
    spinner.fail(chalk.red('Report generation failed'));
    console.error(error);
    process.exit(1);
  }
}

function calculatePosture(results: CheckResult[], baseline: any): PostureScore {
  const categories = {
    secrets: { score: 100, issues: 0 },
    deps: { score: 100, issues: 0 },
    security: { score: 100, issues: 0 },
    tests: { score: 100, issues: 0 },
    config: { score: 100, issues: 0 },
  };

  for (const result of results) {
    const category = result.ruleId.split('.')[0] as keyof typeof categories;
    if (categories[category]) {
      categories[category].issues++;
      const penalty = result.severity === 'error' ? 15 : result.severity === 'warn' ? 5 : 2;
      categories[category].score = Math.max(0, categories[category].score - penalty);
    }
  }

  const overall = Math.round(
    (categories.secrets.score * 0.3 +
      categories.deps.score * 0.25 +
      categories.security.score * 0.2 +
      categories.tests.score * 0.15 +
      categories.config.score * 0.1)
  );

  // Determine trend based on baseline
  let trend: 'improving' | 'stable' | 'declining' | undefined;
  let previousScore: number | undefined;

  if (baseline) {
    previousScore = baseline.entries.length > 0 
      ? Math.max(0, 100 - baseline.entries.length * 3)
      : 100;
    
    if (overall > previousScore + 5) trend = 'improving';
    else if (overall < previousScore - 5) trend = 'declining';
    else trend = 'stable';
  }

  return { overall, categories, trend, previousScore };
}

function printConsoleReport(posture: PostureScore, results: CheckResult[], baselineStats: any): void {
  console.log(chalk.bold.cyan('\n📊 Devrail Security Posture Report\n'));
  console.log(chalk.dim('─'.repeat(50)));

  // Overall score with visual bar
  const scoreColor = posture.overall >= 80 ? chalk.green : posture.overall >= 50 ? chalk.yellow : chalk.red;
  const filledBars = Math.round(posture.overall / 5);
  const emptyBars = 20 - filledBars;
  const bar = scoreColor('█'.repeat(filledBars)) + chalk.dim('░'.repeat(emptyBars));
  
  console.log(`\n${chalk.bold('Overall Score:')} ${scoreColor.bold(posture.overall + '/100')}`);
  console.log(`  ${bar}`);

  // Trend
  if (posture.trend) {
    const trendIcon = posture.trend === 'improving' ? chalk.green('↑') 
      : posture.trend === 'declining' ? chalk.red('↓') 
      : chalk.dim('→');
    console.log(`  ${trendIcon} ${posture.trend} (was ${posture.previousScore})`);
  }

  // Category breakdown
  console.log(chalk.bold('\n📈 Category Breakdown:\n'));

  const cats = [
    { name: 'Secrets Hygiene', key: 'secrets', weight: '30%' },
    { name: 'Dependencies', key: 'deps', weight: '25%' },
    { name: 'Security Patterns', key: 'security', weight: '20%' },
    { name: 'Test Coverage', key: 'tests', weight: '15%' },
    { name: 'Configuration', key: 'config', weight: '10%' },
  ];

  for (const cat of cats) {
    const data = posture.categories[cat.key as keyof typeof posture.categories];
    const color = data.score >= 80 ? chalk.green : data.score >= 50 ? chalk.yellow : chalk.red;
    const miniBar = color('█'.repeat(Math.round(data.score / 10))) + chalk.dim('░'.repeat(10 - Math.round(data.score / 10)));
    console.log(`  ${miniBar} ${color(data.score.toString().padStart(3))} ${cat.name} (${cat.weight})`);
    if (data.issues > 0) {
      console.log(`             ${chalk.dim(`${data.issues} issue(s)`)}`);
    }
  }

  // Baseline status
  if (baselineStats && baselineStats.baselined > 0) {
    console.log(chalk.bold('\n📋 Technical Debt:\n'));
    console.log(`  Baselined issues: ${chalk.yellow(baselineStats.baselined)}`);
    console.log(`  Fixed since baseline: ${chalk.green(baselineStats.fixed)}`);
    console.log(`  New issues: ${chalk.red(baselineStats.new)}`);
  }

  // Top 3 actions
  console.log(chalk.bold('\n🎯 Top Actions:\n'));
  
  const actions: string[] = [];
  if (posture.categories.secrets.issues > 0) {
    actions.push('Fix secret/credential issues');
  }
  if (posture.categories.deps.issues > 0) {
    actions.push('Update vulnerable dependencies');
  }
  if (posture.categories.tests.issues > 0) {
    actions.push('Add unit tests');
  }
  if (posture.categories.security.issues > 0) {
    actions.push('Address security patterns');
  }
  if (posture.categories.config.issues > 0) {
    actions.push('Improve project configuration');
  }

  for (let i = 0; i < Math.min(3, actions.length); i++) {
    console.log(`  ${i + 1}. ${actions[i]}`);
  }

  if (actions.length === 0) {
    console.log(chalk.green('  ✓ No critical actions needed!'));
  }

  console.log();
}

function generateMarkdownReport(posture: PostureScore, results: CheckResult[], baselineStats: any): string {
  const lines: string[] = [];
  
  lines.push('# 📊 Devrail Security Posture Report');
  lines.push('');
  lines.push(`**Date:** ${new Date().toLocaleDateString()}`);
  lines.push(`**Overall Score:** ${posture.overall}/100`);
  
  if (posture.trend) {
    const emoji = posture.trend === 'improving' ? '📈' : posture.trend === 'declining' ? '📉' : '➡️';
    lines.push(`**Trend:** ${emoji} ${posture.trend}`);
  }
  
  lines.push('');
  lines.push('## Category Breakdown');
  lines.push('');
  lines.push('| Category | Score | Issues |');
  lines.push('|----------|-------|--------|');
  
  const cats = [
    { name: 'Secrets Hygiene', key: 'secrets' },
    { name: 'Dependencies', key: 'deps' },
    { name: 'Security Patterns', key: 'security' },
    { name: 'Test Coverage', key: 'tests' },
    { name: 'Configuration', key: 'config' },
  ];
  
  for (const cat of cats) {
    const data = posture.categories[cat.key as keyof typeof posture.categories];
    const emoji = data.score >= 80 ? '✅' : data.score >= 50 ? '⚠️' : '❌';
    lines.push(`| ${cat.name} | ${emoji} ${data.score}/100 | ${data.issues} |`);
  }
  
  if (baselineStats && baselineStats.baselined > 0) {
    lines.push('');
    lines.push('## Technical Debt');
    lines.push('');
    lines.push(`- **Baselined:** ${baselineStats.baselined} issues`);
    lines.push(`- **Fixed:** ${baselineStats.fixed} issues`);
    lines.push(`- **New:** ${baselineStats.new} issues`);
  }
  
  lines.push('');
  lines.push('---');
  lines.push('*Generated by [Devrail](https://devrail.dev)*');
  
  return lines.join('\n');
}
